import { ApiError } from "@brandex/api-client";
import { formatRelative } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Spinner } from "@brandex/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Laptop, Monitor } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { keys, useMe, useRevokeSession, useSessions, useSignOut } from "../queries";

export function SecurityView() {
  const t = useT();
  return (
    <div className="mx-auto grid max-w-3xl gap-6">
      <h1 className="font-display text-3xl font-semibold tracking-tight">{t("nav.security")}</h1>
      <MfaCard />
      <SessionsCard />
      <ChangePasswordCard />
    </div>
  );
}

function SessionsCard() {
  const { locale } = useBrandex();
  const t = useT();
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const signOut = useSignOut();
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>{t("security.sessions")}</CardTitle>
        <Button variant="outline" size="sm" loading={signOut.isPending} onClick={() => signOut.mutate(true)}>
          {t("security.signOutAll")}
        </Button>
      </CardHeader>
      <CardContent>
        {sessions.isPending ? <Spinner label={t("common.loading")} /> : null}
        {sessions.isError ? <Alert tone="error">{errorMessage(sessions.error, t)}</Alert> : null}
        {revoke.isError ? <Alert tone="error">{errorMessage(revoke.error, t)}</Alert> : null}
        <ul className="divide-y divide-border">
          {sessions.data?.map((s) => (
            <li key={s.id} className="flex items-center gap-4 py-3">
              {s.client === "desktop" ? <Monitor className="h-5 w-5 text-muted-foreground" aria-hidden /> : <Laptop className="h-5 w-5 text-muted-foreground" aria-hidden />}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{describeAgent(s.user_agent)}</p>
                <p className="text-xs text-muted-foreground">
                  {s.ip_address ?? "—"} · {t("security.lastActive", { time: formatRelative(locale, s.last_used_at) })}
                </p>
              </div>
              {s.current ? (
                <Badge tone="success">{t("security.thisDevice")}</Badge>
              ) : (
                <Button variant="ghost" size="sm" loading={revoke.isPending && revoke.variables === s.id} onClick={() => revoke.mutate(s.id)}>
                  {t("security.signOutDevice")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** A compact, human description of a user-agent string. */
export function describeAgent(ua: string | null | undefined): string {
  if (ua === null || ua === undefined || ua === "") return "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : /Tauri|BrandEx/i.test(ua) ? "BrandEx Desktop" : null;
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X|Macintosh/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : null;
  if (browser !== null && os !== null) return `${browser} on ${os}`;
  return browser ?? os ?? ua.slice(0, 60);
}

const passwordSchema = z.object({ current: z.string().min(1), next: z.string().min(12).max(128) });

function ChangePasswordCard() {
  const { api } = useBrandex();
  const t = useT();
  const qc = useQueryClient();
  const form = useForm<z.infer<typeof passwordSchema>>({ resolver: zodResolver(passwordSchema), defaultValues: { current: "", next: "" } });
  const change = useMutation({
    mutationFn: async (v: z.infer<typeof passwordSchema>) => {
      await api.http.POST("/v1/auth/password/change", { body: { current_password: v.current, new_password: v.next } });
    },
    onSuccess: () => {
      form.reset();
      void qc.invalidateQueries({ queryKey: keys.sessions });
    },
  });
  return (
    <Card>
      <CardHeader><CardTitle>{t("security.changePassword")}</CardTitle></CardHeader>
      <CardContent>
        <form noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => change.mutate(v))}>
          {change.isSuccess ? <Alert tone="success">{t("security.passwordChanged")}</Alert> : null}
          {change.isError ? <Alert tone="error">{errorMessage(change.error, t)}</Alert> : null}
          <Field label={t("security.currentPassword")}>
            <Input type="password" autoComplete="current-password" {...form.register("current")} />
          </Field>
          <Field label={t("auth.newPassword")} hint={t("auth.passwordHint")} error={form.formState.errors.next ? t("auth.passwordHint") : undefined}>
            <Input type="password" autoComplete="new-password" {...form.register("next")} />
          </Field>
          <div><Button type="submit" loading={change.isPending}>{t("common.save")}</Button></div>
        </form>
      </CardContent>
    </Card>
  );
}

/** Extracts the base32 `secret` parameter from an otpauth:// URI. */
export function totpSecret(uri: string): string | null {
  try {
    return new URL(uri).searchParams.get("secret");
  } catch {
    return null;
  }
}

/** Groups a base32 key in fours so it is easy to type into an app. */
export function groupKey(key: string): string {
  return key.replace(/(.{4})(?=.)/g, "$1 ");
}

export function MfaCard() {
  const { api } = useBrandex();
  const t = useT();
  const qc = useQueryClient();
  const me = useMe();
  const [code, setCode] = useState("");
  const [requested, setRequested] = useState(() => qc.getQueryData(keys.mfaEnrollment) !== undefined);
  // The secret lives in the query cache, not component state: staff pages
  // remount this card when they refetch (window focus, polling), and a fresh
  // secret would no longer match the one already added to the app.
  const start = useQuery({
    queryKey: keys.mfaEnrollment,
    enabled: requested,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data } = await api.http.POST("/v1/auth/mfa/totp/enroll");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const confirm = useMutation({
    mutationFn: async () => {
      if (start.data === undefined) return;
      await api.http.POST("/v1/auth/mfa/totp/confirm", { body: { enrollment_token: start.data.enrollment_token, code } });
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.mfaEnrollment });
      return qc.invalidateQueries({ queryKey: keys.me });
    },
  });
  const enabled = me.data?.mfa_enabled === true;
  const secret = start.data === undefined ? null : totpSecret(start.data.provisioning_uri);
  // A code can only be rejected for one reason at this point; say which.
  const confirmError =
    confirm.error === null
      ? null
      : confirm.error instanceof ApiError && confirm.error.code === "validation_failed" && confirm.error.field === "code"
        ? t("security.mfaCodeRejected")
        : errorMessage(confirm.error, t);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("security.mfa")}</CardTitle>
        <CardDescription>{enabled ? t("security.mfaOn") : t("security.mfaOff")}</CardDescription>
      </CardHeader>
      {!enabled ? (
        <CardContent className="grid gap-4">
          {start.isError ? <Alert tone="error">{errorMessage(start.error, t)}</Alert> : null}
          {start.data === undefined ? (
            <div>
              <Button
                variant="accent"
                loading={requested && start.isFetching}
                onClick={() => (requested ? void start.refetch() : setRequested(true))}
              >
                {t("security.mfaStart")}
              </Button>
            </div>
          ) : (
            <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); confirm.mutate(); }}>
              <p className="text-sm">{t("security.mfaScan")}</p>
              <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
                {/* White quiet zone: authenticator cameras need contrast in dark mode too. */}
                <div className="w-fit rounded-md bg-white p-3">
                  <QRCodeSVG value={start.data.provisioning_uri} size={176} level="M" role="img" aria-label={t("security.mfaQr")} />
                </div>
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs text-muted-foreground">{t("security.mfaKey")}</p>
                  <code className="break-all font-mono text-sm tracking-wider" data-testid="mfa-secret">{secret === null ? start.data.provisioning_uri : groupKey(secret)}</code>
                  <p className="mt-2 text-xs text-muted-foreground">{t("security.mfaKeyHint")}</p>
                </div>
              </div>
              {confirmError === null ? null : <Alert tone="error">{confirmError}</Alert>}
              <Field label={t("auth.totpCode")}>
                <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
              </Field>
              <div><Button type="submit" disabled={code.length !== 6} loading={confirm.isPending}>{t("security.mfaConfirm")}</Button></div>
            </form>
          )}
        </CardContent>
      ) : null}
    </Card>
  );
}
